import { alpha, type ThemeOptions } from '@mui/material/styles'

import type { ThemeColors } from '../types'

export const buttonComponents = (colors: ThemeColors): ThemeOptions['components'] => {
  const containedPrimaryHover = {
    backgroundColor: colors.background.paper,
    color: colors.active[1],
  }

  return {
    MuiButton: {
      defaultProps: {
        color: 'inherit',
        disableRipple: true,
        variant: 'outlined',
      },
      styleOverrides: {
        contained: {
          '&.Mui-disabled': {
            '&.MuiButton-colorInherit, &.MuiButton-colorPrimary': {
              backgroundColor: colors.disabled,
              color: colors.background.paper,
            },
            '&.MuiButton-colorSecondary': {
              backgroundColor: alpha(colors.active[1], 0.05),
              color: colors.disabled,
            },
          },
          '&.Mui-focusVisible': {
            ...containedPrimaryHover,
            boxShadow: undefined,
          },
          // `color` defaults to `inherit` app-wide, so this is what almost
          // every contained button in the app actually renders as — and left
          // alone MUI paints it a flat grey, which the root's primary border
          // then framed. A contained button is the committing action of its
          // dialog or card; it gets the primary fill.
          '&.MuiButton-colorInherit': {
            '&&:active': {
              backgroundColor: colors.active[3],
            },
            '&&:hover': {
              backgroundColor: colors.active[2],
              borderColor: colors.active[2],
            },
            backgroundColor: colors.active[1],
            // The canvas colour, not paper: it is the far end of the ramp from
            // `active[1]` in BOTH themes (near-white on light's teal-600,
            // near-black on dark's teal-400), so the label stays legible
            // without a per-theme branch.
            color: colors.background.default,
          },
          '&.MuiButton-colorPrimary': {
            '&&:active': {
              // Touch taps skip hover and focus-visible, so the pressed state
              // has to carry its own label colour — `active[5]` is near-white
              // teal-100 in light mode.
              ...containedPrimaryHover,
              backgroundColor: colors.active[5],
            },
            '&&:hover': containedPrimaryHover,
          },
          '&.MuiButton-colorSecondary': {
            '&&:active': {
              backgroundColor: alpha(colors.active[1], 0.25),
            },
            '&&:hover': {
              backgroundColor: alpha(colors.active[1], 0.2),
            },
            backgroundColor: alpha(colors.active[1], 0.1),
            color: colors.active[1],
          },
          // Casing, weight, size, radius and padding live on `sizeSmall`; the
          // variants only add fill and border.
          '&.MuiButton-sizeSmall': {
            border: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
          '&:hover': {
            '@media(hover: none)': {
              boxShadow: 'none',
            },
            boxShadow: 'none',
          },
          boxShadow: 'none',
        },
        outlined: {
          '&.Mui-disabled': {
            border: undefined,
            borderColor: colors.disabled,
            color: colors.disabled,
          },
          '&.MuiButton-colorPrimary': {
            '&:active': {
              backgroundColor: colors.active[5],
            },
            '&:hover': {
              backgroundColor: undefined,
              border: undefined,
            },
            '&:hover, &.Mui-focusVisible': {
              borderColor: colors.active[1],
              color: colors.active[1],
            },
            border: undefined,
            color: undefined,
          },
          // 1px less than `sizeSmall` on every edge because of the border.
          '&.MuiButton-sizeSmall': {
            padding: '3px 7px',
          },
          '&:active': {
            backgroundColor: colors.main[5],
          },
          '&:hover': {
            // Unsets border as it will require us to override borderWidth and
            // borderStyle over and over
            border: undefined,
          },
          '&:hover, &.Mui-focusVisible': {
            borderColor: colors.main[1],
          },
          background: colors.background.paper,
          borderColor: colors.main[5],
          borderStyle: 'solid',
          borderWidth: 1,
          // 1px smaller because of border
          padding: '7px 12px',
        },
        root: {
          '&.Mui-disabled': {
            borderColor: colors.disabled,
          },
          '&.Mui-focusVisible': {
            boxShadow: `0 0 0 3px ${alpha(colors.active[1], 0.26)}`,
          },
          '&:active:not(.Mui-disabled)': {
            transform: 'translateY(1px)',
          },
          '&:hover': {
            backgroundColor: undefined,
          },
          border: `1px solid ${colors.active[1]}`,
          borderRadius: 6,
          boxSizing: 'border-box',
          fontSize: '1.4rem',
          fontWeight: 700,
          height: 34,
          letterSpacing: '0.02em',
          lineHeight: 1.55,
          padding: '8px 16px',
          textTransform: 'uppercase',
          transition:
            'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), color 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        sizeLarge: {
          height: 40,
        },
        sizeSmall: {
          borderRadius: 4,
          fontSize: '1.35rem',
          fontWeight: 700,
          gap: 4,
          height: 28,
          padding: '4px 8px',
          textTransform: 'uppercase',
        },
        text: {
          '&&.Mui-disabled': {
            color: colors.disabled,
          },
          '&.MuiButton-colorPrimary': {
            '&:active': {
              background: alpha(colors.active[1], 0.3),
            },
            '&:hover, &.Mui-focusVisible': {
              background: alpha(colors.active[1], 0.1),
            },
          },
          '&.MuiButton-sizeSmall': {
            '&:hover, &.Mui-focusVisible': {
              border: 'none',
            },
            border: 'none',
          },
          '&:active': {
            background: colors.main[5],
          },
          '&:hover, &.Mui-focusVisible': {
            borderColor: colors.main[1],
          },
          borderColor: 'transparent',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        // Same interaction grammar as MuiButton: teal wash on hover, a deeper
        // wash while pressed, and a ring — never just a wash — for keyboard
        // focus. Only the background moves, so `color="error"` icons keep their
        // own colour.
        root: {
          '&&:active:not(.Mui-disabled)': {
            backgroundColor: alpha(colors.active[1], 0.16),
          },
          '&.Mui-disabled': {
            color: colors.disabled,
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(colors.active[1], 0.08),
            boxShadow: `0 0 0 3px ${alpha(colors.active[1], 0.26)}`,
          },
          '&:hover': {
            backgroundColor: alpha(colors.active[1], 0.08),
          },
          transition:
            'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        sizeSmall: {
          '& .MuiSvgIcon-root': {
            fontSize: '2rem',
          },
        },
      },
    },
  }
}
